import Button from "./ds/Button";
import Icon from "./ds/Icon";
import Modal from "./ds/Modal";
import { useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import {
  getPayoutDetails,
  savePayoutDetails,
  requestWithdrawal,
} from "../api/payouts";

/**
 * Withdraw to a bank account.
 *
 * Two steps, because most people arrive without bank details saved: collect them
 * once, then withdraw. Someone who already has details lands straight on the amount.
 *
 * @param {{ open: boolean, onClose: () => void, available: number, isDark: boolean, onDone?: () => void }} props
 */
const WithdrawModal = ({ open, onClose, available = 0, isDark, onDone }) => {
  const [step, setStep] = useState("loading"); // loading | details | amount | done
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const [iban, setIban] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [identityNumber, setIdentityNumber] = useState("");
  const [maskedIban, setMaskedIban] = useState("");
  const [amount, setAmount] = useState("");
  /** What a withdrawal costs, as the server reports it: { flat, percent, minAmount }. */
  const [fee, setFee] = useState(null);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const field = 
    "w-full rounded-[22px] bg-fill-tertiary px-space-16 py-[10px] text-callout text-label-primary outline-none placeholder:text-label-tertiary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]";
  const label = "mb-space-4 block text-footnote-semibold text-label-secondary";

  useEffect(() => {
    if (!open) return;
    setError("");
    setResult(null);
    setAmount("");
    setStep("loading");

    getPayoutDetails()
      .then((res) => {
        const d = res.data || {};
        setBeneficiaryName(d.beneficiaryName || "");
        setIdentityNumber(d.identityNumber || "");
        setMaskedIban(d.ibanMasked || "");
        setFee(d.fee || null);
        setStep(d.hasIban ? "amount" : "details");
      })
      .catch(() => setStep("details"));
  }, [open]);

  if (!open) return null;

  const handleSaveDetails = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await savePayoutDetails({ iban, beneficiaryName, identityNumber });
      const res = await getPayoutDetails();
      setMaskedIban(res.data?.ibanMasked || "");
      setStep("amount");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save your bank details.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * What this withdrawal costs and what lands in their account.
   *
   * Mirrors the server's own split so the figure shown is the figure charged.
   * @see mintly-back/src/services/withdrawalFees
   */
  const quote = (value) => {
    const charged = Math.min(value, fee?.flat || 0);
    return { charged, net: Math.round((value - charged) * 100) / 100 };
  };

  const typed = parseFloat(String(amount).replace(",", ".")) || 0;
  const preview = typed > 0 ? quote(typed) : null;

  const handleWithdraw = async (e) => {
    e.preventDefault();
    setError("");
    const value = parseFloat(String(amount).replace(",", "."));
    if (!value || value <= 0) {
      setError("Enter an amount greater than 0.");
      return;
    }
    if (value > available) {
      setError(`You have ${available.toFixed(2)} ₾ available.`);
      return;
    }
    if (fee?.minAmount && value < fee.minAmount) {
      setError(`The smallest withdrawal is ${fee.minAmount.toFixed(2)} ₾.`);
      return;
    }
    if (fee?.maxAmount && value > fee.maxAmount) {
      setError(`The largest withdrawal is ${fee.maxAmount.toFixed(2)} ₾.`);
      return;
    }
    if (quote(value).net <= 0) {
      setError(`Withdraw at least ${(fee?.minAmount || 0).toFixed(2)} ₾ — the fee would take all of it.`);
      return;
    }

    setSaving(true);
    try {
      const res = await requestWithdrawal(value);
      setResult(res);
      setStep("done");
      // The balance already moved server-side; tell the page to re-read it.
      window.dispatchEvent(new CustomEvent("balance-updated"));
      onDone?.();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Could not request the withdrawal. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      closeLabel="Close"
      isDark={isDark}
      title={step === "details" ? "Add your bank account" : step === "done" ? "Withdrawal requested" : "Withdraw"}
      description={
        step === "amount" ? `${available.toFixed(2)} ₾ available` : step === "details" ? "We only need this once." : undefined
      }
    >
        {error && (
          <p className="rounded-radius-sm bg-status-danger-bg px-space-12 py-space-8 text-footnote text-status-danger">{error}</p>
        )}

        {step === "loading" && (
          <div className="flex justify-center py-10">
            <FiLoader className="h-6 w-6 text-accent-text animate-spin" />
          </div>
        )}

        {step === "details" && (
          <form onSubmit={handleSaveDetails} className="space-y-4">
            <div>
              <label className={label} htmlFor="wd-iban">IBAN</label>
              <input
                id="wd-iban"
                className={field}
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase())}
                placeholder="GE29TB0000000123456789"
                autoComplete="off"
              />
              <p className={`text-footnote ${mutedText} mt-1.5`}>
                Georgian accounts only — GE followed by 20 characters.
              </p>
            </div>
            <div>
              <label className={label} htmlFor="wd-name">Account holder</label>
              <input
                id="wd-name"
                className={field}
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder="As your bank has it"
              />
              <p className={`text-footnote ${mutedText} mt-1.5`}>
                Must match your bank's records, or the transfer is rejected.
              </p>
            </div>
            <div>
              <label className={label} htmlFor="wd-id">
                Personal number <span className="normal-case font-normal">(optional)</span>
              </label>
              <input
                id="wd-id"
                className={field}
                value={identityNumber}
                onChange={(e) => setIdentityNumber(e.target.value.replace(/\D/g, ""))}
                placeholder="11 digits"
                inputMode="numeric"
              />
            </div>
            <Button type="submit" fullWidth loading={saving}>
              Save and continue
            </Button>
          </form>
        )}

        {step === "amount" && (
          <form onSubmit={handleWithdraw} className="space-y-4">
            <div>
              <label className={label} htmlFor="wd-amount">Amount</label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-subheadline ${mutedText}`}>₾</span>
                <input
                  id="wd-amount"
                  className={`${field} pl-8`}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
                  placeholder="0.00"
                  inputMode="decimal"
                  autoFocus
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  onClick={() => setAmount(String(available.toFixed(2)))}
                  className="text-footnote-semibold text-accent-text hover:text-accent-text transition"
                >
                  Withdraw everything
                </button>
                {fee?.minAmount > 0 && (
                  <span className={`text-footnote ${mutedText}`}>
                    min {fee.minAmount.toFixed(2)} ₾
                  </span>
                )}
              </div>
            </div>

            {preview && preview.charged > 0 && (
              <div className="rounded-radius-md bg-bg-inset p-space-12">
                <div className="flex items-center justify-between text-footnote">
                  <span className={mutedText}>Withdrawal fee</span>
                  <span className={baseText}>−{preview.charged.toFixed(2)} ₾</span>
                </div>
                <div className="flex items-center justify-between text-subheadline-semibold mt-1">
                  <span className={mutedText}>You'll receive</span>
                  <span className={baseText}>{preview.net.toFixed(2)} ₾</span>
                </div>
              </div>
            )}

            <div className="rounded-radius-md bg-bg-inset p-space-12">
              <p className={`text-footnote ${mutedText}`}>Going to</p>
              <p className={`text-subheadline-semibold ${baseText} mt-0.5`}>{maskedIban}</p>
              <p className={`text-footnote ${mutedText}`}>{beneficiaryName}</p>
              <button
                type="button"
                onClick={() => { setIban(""); setStep("details"); }}
                className="text-footnote-semibold text-accent-text hover:text-accent-text transition mt-2"
              >
                Use a different account
              </button>
            </div>

            <Button type="submit" fullWidth loading={saving}>
              Withdraw
            </Button>
            <p className={`text-footnote ${mutedText} text-center`}>
              Bank transfers usually clear within one business day. If one is rejected,
              the money goes back to your balance automatically.
            </p>
          </form>
        )}

        {step === "done" && (
          <div className="flex flex-col items-center py-6">
            <span className="mb-space-12 flex size-[64px] items-center justify-center rounded-full bg-icon-badge-bg text-icon-badge-fg">
              <Icon name="check" size={28} />
            </span>
            <p className={`text-title-1 ${baseText}`}>
              {(result?.data?.amount ?? 0).toFixed(2)} ₾
            </p>
            <p className={`text-subheadline ${mutedText} mt-2 text-center max-w-xs`}>
              {result?.message ||
                "Withdrawal requested. Bank transfers usually clear within one business day."}
            </p>
            <Button size="m" className="mt-space-24" onClick={onClose}>
              Done
            </Button>
          </div>
        )}
    </Modal>
  );
};

export default WithdrawModal;
